import { Plane, Brush } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { Button, SectionBreakCurve, Dropdown } from '@/components';
import { env } from '@/config/env';
import {
  CalendarItem,
  CalendarItemSkeleton,
  CalendarEmptyState,
} from '@/features/calendar/components';
import { usePageView } from '@/lib/analytics';
import { useIsMobile } from '@/lib/hooks';
import { getSessionLabel } from '@/lib/utils';
import { useSchedulePageStore } from '@/stores/schedule-page';

import { useScheduleData } from './api/queries';
import { Schedule } from './types';

const SESSION_LABEL = getSessionLabel();

const getDisplayStatus = (
  lesson: Schedule.Lesson,
  globalIndex: number,
  firstUpcomingIndex: number
) => {
  const isFirstUpcoming =
    lesson.status === 'upcoming' && globalIndex === firstUpcomingIndex;
  const shouldShowStatus = lesson.status !== 'upcoming' || isFirstUpcoming;

  let displayStatus: 'upcoming' | 'holiday' | 'missed' | 'attended' | undefined;

  if (isFirstUpcoming) {
    displayStatus = 'upcoming';
  } else if (lesson.status === 'upcoming') {
    displayStatus = undefined;
  } else if (lesson.status === 'holiday') {
    displayStatus = 'holiday';
  } else {
    displayStatus = lesson.status;
  }

  return { displayStatus, shouldShowStatus };
};

const stripYearFromDate = (dateString: string): string => {
  // Parse the date and reformat without year
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  });
};

const renderMonthLessons = (
  monthLessons: Schedule.Lesson[],
  sortedLessons: Schedule.Lesson[],
  firstUpcomingIndex: number
) => {
  return monthLessons.map((lesson, _monthIndex) => {
    // Find the lesson's index in the original sorted array
    const globalIndex = sortedLessons.findIndex(
      l =>
        l.name === lesson.name &&
        l.date === lesson.date &&
        l.time === lesson.time
    );

    const { displayStatus, shouldShowStatus } = getDisplayStatus(
      lesson,
      globalIndex,
      firstUpcomingIndex
    );

    return (
      <CalendarItem
        key={`${lesson.name}-${lesson.date}-${globalIndex}`}
        data-calendar-index={globalIndex}
        title={lesson.name}
        date={stripYearFromDate(lesson.date)}
        day={lesson.day}
        time={lesson.time}
        status={displayStatus}
        variant={lesson.variant}
        start_time={lesson.start_time ?? 0}
        joiningLink={lesson.joiningLink ?? ''}
        shouldShowStatus={shouldShowStatus}
      />
    );
  });
};

export default function Calendar() {
  usePageView('Schedule');
  const isMobile = useIsMobile();

  const {
    selectedMonth,
    setSelectedMonth,
    filter,
    setFilter,
    resetFilters,
    isOpen,
  } = useSchedulePageStore();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { lessons, pastLessons, isLoading, timezone } = useScheduleData();

  // Generate dropdown options from lessons data
  const dropdownOptions =
    lessons.length > 0
      ? (() => {
          const monthYears = new Set<string>();
          for (const lesson of lessons) {
            const lessonDate = new Date(lesson.date);
            const monthYear = lessonDate.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            });
            monthYears.add(monthYear);
          }
          const monthOptions = [...monthYears]
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
            .map(monthYear => ({ label: monthYear, value: monthYear }));

          return [
            { label: 'Select all', value: 'Select all' },
            ...monthOptions,
          ];
        })()
      : [{ label: 'Select all', value: 'Select all' }];

  useLayoutEffect(() => {
    if (scrollContainerRef.current && pastLessons.length > 0) {
      // Find the index of (upcoming lesson - 1)
      const targetIndex = Math.max(0, pastLessons.length - 2);

      setTimeout(() => {
        if (scrollContainerRef.current) {
          // Find the calendar item element with the target index
          const targetElement = scrollContainerRef.current.querySelector(
            `[data-calendar-index="${targetIndex}"]`
          );

          if (targetElement) {
            // Get the position of the target element relative to the scroll container
            const containerRect =
              scrollContainerRef.current.getBoundingClientRect();
            const targetRect = targetElement.getBoundingClientRect();
            const scrollOffset = targetRect.top - containerRect.top;

            // Scroll only the scroll container, not parent containers
            scrollContainerRef.current.scrollTop =
              scrollContainerRef.current.scrollTop + scrollOffset;
          }
        }
      }, 100);
    }
  }, [pastLessons]);

  // Reset filters when drawer closes (isOpen changes from true to false)
  useEffect(() => {
    if (!isOpen) {
      resetFilters();
    }
  }, [isOpen, resetFilters]);

  return (
    <div className="w-full flex flex-col">
      {/* Header */}
      <div className="flex items-left justify-between flex-col lg:flex-row">
        <h1 className="text-2xl font-bold">All {SESSION_LABEL.capitalize}s</h1>
        {timezone ? (
          <p className="text-sm text-magical-moonlight-d1">
            All mentioned times are in&nbsp;
            <span className="text-magical-moonlight-d2">{timezone}</span>
          </p>
        ) : null}
      </div>

      {/* Divider */}
      <SectionBreakCurve
        bottomColor="transparent"
        strokeColor="var(--color-magical-moonlight-d1)"
        strokeWidth={1}
        className="my-3 lg:my-6"
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-start gap-4">
        <div>
          <Dropdown
            size={isMobile ? 'sm' : 'default'}
            options={dropdownOptions}
            value={selectedMonth}
            onValueChange={setSelectedMonth}
          />
        </div>

        <Button
          variant={filter?.status === 'holiday' ? 'default' : 'outline'}
          size={isMobile ? 'sm' : 'default'}
          icon={<Plane />}
          onClick={() => setFilter({ status: 'holiday' })}
        >
          Holiday
        </Button>

        {env.IS_OFFLINE ? null : (
          <Button
            variant={filter?.variant === 'hobby_class' ? 'default' : 'outline'}
            icon={<Brush />}
            onClick={() => setFilter({ variant: 'hobby_class' })}
            size={isMobile ? 'sm' : 'default'}
          >
            Hobby Class
          </Button>
        )}
      </div>

      {/* Calendar List */}
      <div className="flex-1 mt-3 lg:mt-6">
        <div
          ref={scrollContainerRef}
          className="h-[600px] overflow-x-hidden overflow-y-auto scrollbar-thin scrollbar-thumb-magical-moonlight-d1 scrollbar-track-transparent"
        >
          <div className="space-y-3 mb-48">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <CalendarItemSkeleton key={index} />
                ))}
              </div>
            ) : (
              (() => {
                // Create a copy and sort by date and time
                let sortedLessons = structuredClone(lessons);

                // Apply holiday filter if active
                if (filter?.status || filter?.variant) {
                  sortedLessons = sortedLessons.filter(lesson => {
                    if (filter?.status) {
                      return lesson.status === filter.status;
                    }
                    if (filter?.variant) {
                      return lesson.variant === filter.variant;
                    }
                    return false;
                  });
                }

                sortedLessons.sort((a, b) => {
                  // Parse date and time for comparison
                  const dateA = new Date(`${a.date} ${a.time}`);
                  const dateB = new Date(`${b.date} ${b.time}`);
                  return dateA.getTime() - dateB.getTime();
                });

                // Group lessons by month
                const groupedByMonth: {
                  [month: string]: typeof sortedLessons;
                } = {};
                for (const lesson of sortedLessons) {
                  const lessonDate = new Date(lesson.date);
                  const monthYear = lessonDate.toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                  });

                  if (!groupedByMonth[monthYear]) {
                    groupedByMonth[monthYear] = [];
                  }
                  groupedByMonth[monthYear].push(lesson);
                }

                // Filter grouped lessons based on selected month
                const filteredGroupedByMonth =
                  selectedMonth === 'Select all'
                    ? groupedByMonth
                    : { [selectedMonth]: groupedByMonth[selectedMonth] || [] };

                // Check if there are any lessons after filtering
                const hasFilteredResults = Object.values(
                  filteredGroupedByMonth
                ).some(monthLessons => monthLessons && monthLessons.length > 0);

                // Show empty state if filters are active and no results
                const hasActiveFilters =
                  !!filter?.status ||
                  !!filter?.variant ||
                  selectedMonth !== 'Select all';

                // Show empty state if no lessons at all OR if filters are active and no results
                if (
                  lessons.length === 0 ||
                  (!hasFilteredResults && hasActiveFilters)
                ) {
                  return (
                    <CalendarEmptyState
                      filter={filter}
                      selectedMonth={selectedMonth}
                    />
                  );
                }

                // Find the first upcoming lesson globally for status handling
                const firstUpcomingIndex = sortedLessons.findIndex(
                  l => l.status === 'upcoming'
                );

                return Object.entries(filteredGroupedByMonth).map(
                  ([monthYear, monthLessons]) => (
                    <div key={monthYear} className="mb-8">
                      {/* Month Heading */}
                      <div className="sticky top-0 bg-white mb-4 py-2 -mx-4 px-4 z-10">
                        <h2 className="text-md lg:text-lg font-semibold text-magical-moonlight-d2">
                          {monthYear}
                        </h2>
                      </div>

                      {/* Lessons for this month */}
                      <div className="space-y-3">
                        {renderMonthLessons(
                          monthLessons,
                          sortedLessons,
                          firstUpcomingIndex
                        )}
                      </div>
                    </div>
                  )
                );
              })()
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
